/* eslint-disable */
/**
 * @deprecated imagingService.ts — PHASE 2 DISABLED
 *
 * MODULE_IMAGING=false in .env (backend + frontend).
 * Imaging orders from consultations remain via consultationImagingOrderService.
 * This service is for imaging department/study management — now soft-disabled.
 *
 * Rollback: set VITE_MODULE_IMAGING=true + MODULE_IMAGING=true + config:clear
 */

import apiClient from './apiClient';

// ==================== Types ====================

export interface ImagingDepartment {
    id: number;
    name: string;
    code: string;
    description?: string;
    location?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface ImagingStudy {
    id: number;
    name: string;
    code: string;
    department_id: number;
    department?: ImagingDepartment;
    description?: string;
    preparation_instructions?: string;
    duration_minutes: number;
    requires_contrast: boolean;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface StudyTemplate {
    id: number;
    study_id: number;
    name: string;
    template_content?: string;
    findings_template?: string;
    impression_template?: string;
    is_default: boolean;
    is_active: boolean;
}

export type ImagingOrderStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface ImagingOrder {
    id: number;
    patient_id: number;
    study_id: number;
    admission_id?: number;
    doctor_id: number;
    requested_by: number;
    patient?: any;
    study?: ImagingStudy;
    doctor?: any;
    clinical_indication?: string;
    observations?: string;
    status: ImagingOrderStatus;
    ordered_at: string;
    completed_at?: string;
    result?: ImagingResult;
    created_at?: string;
    updated_at?: string;
}

export type ImagingResultStatus = 'draft' | 'final' | 'amended' | 'verified';

export interface ImagingResult {
    id: number;
    order_id: number;
    template_id?: number;
    radiologist_id: number;
    findings?: string;
    impression?: string;
    recommendations?: string;
    bi_rads_category?: string;
    technical_quality?: any;
    report_text?: string;
    images?: number[];
    status: ImagingResultStatus;
    reported_at?: string;
    verified_at?: string;
    order?: ImagingOrder;
    radiologist?: any;
    template?: StudyTemplate;
    files?: ImagingFile[];
    created_at?: string;
    updated_at?: string;
}

export interface ImagingFile {
    id: number;
    result_id: number;
    file_name: string;
    original_name: string;
    mime_type: string;
    file_path: string;
    file_size: number;
    sequence_order: number;
    description?: string;
    modality?: string;
    is_primary: boolean;
    series_instance_uid?: string;
    instance_number?: number;
    annotations?: ImagingAnnotation[];
    measurements?: ImagingMeasurement[];
    created_at?: string;
    updated_at?: string;
    url?: string;
}

export interface ImagingAnnotation {
    id: number;
    file_id: number;
    user_id: number;
    user?: any;
    type: 'line' | 'arrow' | 'ellipse' | 'rectangle' | 'text' | 'freehand';
    coordinates: any;
    label?: string;
    notes?: string;
    color: string;
    created_at?: string;
}

export interface ImagingMeasurement {
    id: number;
    file_id: number;
    user_id: number;
    user?: any;
    type: 'distance' | 'angle' | 'area' | 'ellipse' | 'Cobb';
    coordinates: any;
    value: number;
    unit: string;
    notes?: string;
    created_at?: string;
}

export interface ImagingViewerStatistics {
    total_studies: number;
    total_files: number;
    total_annotations: number;
    total_measurements: number;
    by_status: Record<string, number>;
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

// ==================== Imaging Department Service ====================

export const imagingDepartmentService = {
    async getAll(params: { per_page?: number; search?: string; is_active?: boolean } = {}): Promise<PaginatedResponse<ImagingDepartment>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<PaginatedResponse<ImagingDepartment>>(`/imaging-departments?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<ImagingDepartment> {
        const response = await apiClient.get<ApiResponse<ImagingDepartment>>(`/imaging-departments/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        description?: string;
        location?: string;
        is_active?: boolean;
    }): Promise<ImagingDepartment> {
        const response = await apiClient.post<ApiResponse<ImagingDepartment>>('/imaging-departments', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        description: string;
        location: string;
        is_active: boolean;
    }>): Promise<ImagingDepartment> {
        const response = await apiClient.put<ApiResponse<ImagingDepartment>>(`/imaging-departments/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/imaging-departments/${id}`);
    },
};

// ==================== Imaging Study Service ====================

export const imagingStudyService = {
    async getAll(params: {
        per_page?: number;
        search?: string;
        department_id?: number;
        is_active?: boolean;
    } = {}): Promise<PaginatedResponse<ImagingStudy>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.department_id) queryParams.append('department_id', params.department_id.toString());
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<PaginatedResponse<ImagingStudy>>(`/imaging-studies?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<ImagingStudy> {
        const response = await apiClient.get<ApiResponse<ImagingStudy>>(`/imaging-studies/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        department_id: number;
        description?: string;
        preparation_instructions?: string;
        duration_minutes?: number;
        requires_contrast?: boolean;
        is_active?: boolean;
    }): Promise<ImagingStudy> {
        const response = await apiClient.post<ApiResponse<ImagingStudy>>('/imaging-studies', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        department_id: number;
        description: string;
        preparation_instructions: string;
        duration_minutes: number;
        requires_contrast: boolean;
        is_active: boolean;
    }>): Promise<ImagingStudy> {
        const response = await apiClient.put<ApiResponse<ImagingStudy>>(`/imaging-studies/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/imaging-studies/${id}`);
    },

    async getTemplates(studyId: number): Promise<StudyTemplate[]> {
        const response = await apiClient.get<ApiResponse<StudyTemplate[]>>(`/imaging-studies/${studyId}/templates`);
        return response.data.data;
    },
};

// ==================== Imaging Order Service ====================

export const imagingOrderService = {
    async getAll(params: {
        per_page?: number;
        patient_id?: number;
        study_id?: number;
        status?: ImagingOrderStatus;
        date_from?: string;
        date_to?: string;
    } = {}): Promise<PaginatedResponse<ImagingOrder>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.patient_id) queryParams.append('patient_id', params.patient_id.toString());
        if (params.study_id) queryParams.append('study_id', params.study_id.toString());
        if (params.status) queryParams.append('status', params.status);
        if (params.date_from) queryParams.append('date_from', params.date_from);
        if (params.date_to) queryParams.append('date_to', params.date_to);

        const response = await apiClient.get<PaginatedResponse<ImagingOrder>>(`/imaging-orders?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<ImagingOrder> {
        const response = await apiClient.get<ApiResponse<ImagingOrder>>(`/imaging-orders/${id}`);
        return response.data.data;
    },

    async create(data: {
        patient_id: number;
        study_id: number;
        admission_id?: number;
        doctor_id: number;
        clinical_indication?: string;
        observations?: string;
    }): Promise<ImagingOrder> {
        const response = await apiClient.post<ApiResponse<ImagingOrder>>('/imaging-orders', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        clinical_indication: string;
        observations: string;
        status: ImagingOrderStatus;
    }>): Promise<ImagingOrder> {
        const response = await apiClient.put<ApiResponse<ImagingOrder>>(`/imaging-orders/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/imaging-orders/${id}`);
    },

    async getPending(): Promise<ImagingOrder[]> {
        const response = await apiClient.get<ApiResponse<ImagingOrder[]>>('/imaging-orders/pending');
        return response.data.data;
    },

    async getResult(orderId: number): Promise<ImagingResult> {
        const response = await apiClient.get<ApiResponse<ImagingResult>>(`/imaging-orders/${orderId}/result`);
        return response.data.data;
    },

    async createResult(orderId: number, data: {
        template_id?: number;
        radiologist_id: number;
        findings?: string;
        impression?: string;
        recommendations?: string;
        bi_rads_category?: string;
        technical_quality?: any;
        report_text?: string;
        status?: ImagingResultStatus;
    }): Promise<ImagingResult> {
        const response = await apiClient.post<ApiResponse<ImagingResult>>(`/imaging-orders/${orderId}/result`, data);
        return response.data.data;
    },
};

// ==================== Imaging Result Service ====================

export const imagingResultService = {
    async getAll(params: {
        per_page?: number;
        status?: ImagingResultStatus;
        radiologist_id?: number;
        date_from?: string;
        date_to?: string;
    } = {}): Promise<PaginatedResponse<ImagingResult>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.status) queryParams.append('status', params.status);
        if (params.radiologist_id) queryParams.append('radiologist_id', params.radiologist_id.toString());
        if (params.date_from) queryParams.append('date_from', params.date_from);
        if (params.date_to) queryParams.append('date_to', params.date_to);

        const response = await apiClient.get<PaginatedResponse<ImagingResult>>(`/imaging-results?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<ImagingResult> {
        const response = await apiClient.get<ApiResponse<ImagingResult>>(`/imaging-results/${id}`);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        template_id: number;
        radiologist_id: number;
        findings: string;
        impression: string;
        recommendations: string;
        bi_rads_category: string;
        technical_quality: any;
        report_text: string;
        status: ImagingResultStatus;
    }>): Promise<ImagingResult> {
        const response = await apiClient.put<ApiResponse<ImagingResult>>(`/imaging-results/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/imaging-results/${id}`);
    },
};

// ==================== Imaging Viewer Service ====================

export const imagingViewerService = {
    async getOrders(params: { patient_id?: number; status?: string; study_id?: number; page?: number } = {}): Promise<PaginatedResponse<ImagingOrder>> {
        const queryParams = new URLSearchParams();
        if (params.patient_id) queryParams.append('patient_id', params.patient_id.toString());
        if (params.status) queryParams.append('status', params.status);
        if (params.study_id) queryParams.append('study_id', params.study_id.toString());
        if (params.page) queryParams.append('page', params.page.toString());

        const response = await apiClient.get<PaginatedResponse<ImagingOrder>>(`/imaging-viewer/orders?${queryParams.toString()}`);
        return response.data;
    },

    async getOrder(id: number): Promise<ImagingOrder> {
        const response = await apiClient.get<ApiResponse<ImagingOrder>>(`/imaging-viewer/orders/${id}`);
        return response.data.data;
    },

    async uploadFile(resultId: number, file: File, data: { description?: string; modality?: string; is_primary?: boolean }): Promise<ImagingFile> {
        const formData = new FormData();
        formData.append('file', file);
        if (data.description) formData.append('description', data.description);
        if (data.modality) formData.append('modality', data.modality);
        if (data.is_primary !== undefined) formData.append('is_primary', String(data.is_primary));

        const response = await apiClient.post<ApiResponse<ImagingFile>>(`/imaging-viewer/results/${resultId}/files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data.data;
    },

    async deleteFile(id: number): Promise<void> {
        await apiClient.delete(`/imaging-viewer/files/${id}`);
    },

    async updateFileOrder(id: number, data: { sequence_order: number; is_primary?: boolean }): Promise<ImagingFile> {
        const response = await apiClient.patch<ApiResponse<ImagingFile>>(`/imaging-viewer/files/${id}/order`, data);
        return response.data.data;
    },

    async getAnnotations(fileId: number): Promise<{ annotations: ImagingAnnotation[]; measurements: ImagingMeasurement[] }> {
        const response = await apiClient.get<ApiResponse<{ annotations: ImagingAnnotation[]; measurements: ImagingMeasurement[] }>>(`/imaging-viewer/files/${fileId}/annotations`);
        return response.data.data;
    },

    async addAnnotation(fileId: number, data: {
        type: string;
        coordinates: any;
        label?: string;
        notes?: string;
        color?: string;
    }): Promise<ImagingAnnotation> {
        const response = await apiClient.post<ApiResponse<ImagingAnnotation>>(`/imaging-viewer/files/${fileId}/annotations`, data);
        return response.data.data;
    },

    async deleteAnnotation(id: number): Promise<void> {
        await apiClient.delete(`/imaging-viewer/annotations/${id}`);
    },

    async addMeasurement(fileId: number, data: {
        type: string;
        coordinates: any;
        value: number;
        unit?: string;
        notes?: string;
    }): Promise<ImagingMeasurement> {
        const response = await apiClient.post<ApiResponse<ImagingMeasurement>>(`/imaging-viewer/files/${fileId}/measurements`, data);
        return response.data.data;
    },

    async deleteMeasurement(id: number): Promise<void> {
        await apiClient.delete(`/imaging-viewer/measurements/${id}`);
    },

    async getStatistics(): Promise<ImagingViewerStatistics> {
        const response = await apiClient.get<ApiResponse<ImagingViewerStatistics>>('/imaging-viewer/statistics');
        return response.data.data;
    },

    getFileUrl(filePath: string): string {
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
        return `${baseUrl}/api/v1/imaging-viewer/files/${filePath}`;
    },
};

export default {
    imagingDepartmentService,
    imagingStudyService,
    imagingOrderService,
    imagingResultService,
    imagingViewerService,
};
